'use client'

import React, { FC, ReactElement } from 'react'
import Link from 'next/link'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import { emitCanvasEvent, emitHeaderEvent, type CanvasState } from '@/utils/hooks/useBitmap'
import { Search, ArrowLeft, Globe, Grid3x3 } from 'lucide-react'

const _BITMAP_HEADER_EVENT = 'bitmap-header-event'

const BitmapHeader: FC<{ slug: string }> = (props): ReactElement => {
	const loggedIn = useLoggedIn()

	const [tab, setTab] = React.useState('show-all')
	const [iconSelected, setIconSelected] = React.useState('market-icon')

	const handleTabChange = React.useCallback((value: string) => {
		setTab(value)
		emitCanvasEvent({ state: value as CanvasState })
		emitHeaderEvent({ 
			tab: value as CanvasState,
			focusMyLands: value === 'my-lands'
		})
	}, [])

	const handleViewChange = React.useCallback((isGlobe: boolean) => {
		const icon = isGlobe ? 'globe-icon' : 'market-icon'
		emitHeaderEvent({ type: icon })
		setIconSelected(icon)
	}, [])

	const [value, setValue] = React.useState('')

	const handleChangeInput = React.useCallback((evt: any) => {
		try {
			const val = evt.target.value
			if (val === '') {
				return setValue('')
			}

			const num = parseInt(val)
			if (num >= 0) {
				setValue(num.toString())
			}
		} catch {}
	}, [])

	const handleSubmit = React.useCallback(
		(evt: any) => {
			evt.preventDefault()
			evt.stopPropagation()
			if (value) {
				emitHeaderEvent({ block: parseInt(value) })
				setValue('')
			}
		},
		[value]
	)

	const name = { bitmap: 'Bitmap', natmap: '$NAT' }[props.slug]

	const tabs = [
		{ value: 'show-all', label: 'All' },
		{ value: 'for-sale', label: 'For Sale' },
		...(loggedIn ? [{ value: 'my-lands', label: 'My Lands' }] : [])
	]

	return (
        <header className="shrink-0 border-b border-border bg-card text-foreground" aria-label="Bitmap map controls">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-3 sm:px-6">
                <Link href={`/collection/${props.slug}`} className="inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-foreground hover:text-purple-600 dark:hover:text-purple-400" aria-label={`Back to ${name} collection`}>
                    <ArrowLeft className="size-4" /><span>{name} map</span>
                </Link>
                <div className="ml-auto inline-flex rounded-lg bg-muted p-1 sm:ml-0" role="group" aria-label="Map display">
                    {[false, true].map(globe => <button key={String(globe)} type="button" aria-pressed={(iconSelected === 'globe-icon') === globe} onClick={() => handleViewChange(globe)} className={cn('inline-flex min-h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-ring', (iconSelected === 'globe-icon') === globe ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                        {globe ? <Globe className="size-3.5" /> : <Grid3x3 className="size-3.5" />}{globe ? 'Globe' : 'Grid'}
                    </button>)}
                </div>
                <div className="flex rounded-lg bg-muted p-1" role="group" aria-label="Map listings">
                    {tabs.map(t => <button key={t.value} type="button" aria-pressed={tab === t.value} onClick={() => handleTabChange(t.value)} className={cn('min-h-8 rounded-md px-3 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-ring', tab === t.value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>{t.label}</button>)}
                </div>
                <form onSubmit={handleSubmit} className="min-w-0 flex-1 sm:max-w-[200px]">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                        <Input aria-label="Find block number" inputMode="numeric" placeholder="Block #" value={value} onChange={handleChangeInput} className="h-9 w-full rounded-lg border-border bg-background pl-8 text-xs" />
                    </div>
                </form>
                <div className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-muted-foreground xl:ml-auto xl:w-auto" aria-label="Map legend">
                    <span className="inline-flex items-center gap-1.5"><span className="size-2 rounded-full bg-[#FF8C00]" />Block</span>
                    <span className="inline-flex items-center gap-1.5"><span className="size-2 rounded-full bg-[#00FF00]" />Listed</span>
                    <span className="inline-flex items-center gap-1.5"><span className="size-2 rounded-full bg-[#178EFE]" />Owned</span>
                    <span className="border-l border-border pl-4">Lighter listings: under $20</span>
                </div>
            </div>
            {props.slug === 'natmap' && <p className="border-t border-border px-4 py-2 text-xs text-amber-700 dark:text-amber-400">Experimental indexing. Some unclaimed blocks may have pending claims. <a href="https://geniidata.com/user/gravity/tap-nat-checker" target="_blank" rel="noreferrer" className="underline">Verify manually</a> before inscribing.</p>}
        </header>
    )
}

export default BitmapHeader
